import { useEffect, useMemo, useState } from 'react';

import { YG_DEV_MISSING_SENSORS } from '@/utils/YG_DEV_MISSING_SENSORS';

import { sensorService, hubSensorService } from '@/controllers/sensorService';

import { useSensorsContext } from '@/contexts/SensorsContext';
import { useSystemConfigContext } from '@/contexts/SystemConfigContext';

import { BuildTuple } from '@/types/BuildTuple';

type CalibrationStatus = 'notStarted' | 'success' | 'error';

export type CalibrationStatusArray = BuildTuple<CalibrationStatus, 15> | BuildTuple<CalibrationStatus, 10>;

const initialNoHubCalibrationData: CalibrationStatusArray = [
  'notStarted',
  'notStarted',
  'notStarted',
  'notStarted',
  'notStarted',
  'notStarted',
  'notStarted',
  'notStarted',
  'notStarted',
  'notStarted',
  'notStarted',
  'notStarted',
  'notStarted',
  'notStarted',
  'notStarted',
] as const;

const initialHubCalibrationData: CalibrationStatusArray = [
  'notStarted',
  'notStarted',
  'notStarted',
  'notStarted',
  'notStarted',
  'notStarted',
  'notStarted',
  'notStarted',
  'notStarted',
  'notStarted',
] as const;

export function useSensor(sensorId?: string, isHubFlow = false) {
  const [calibrationData, setCalibrationData] = useState<CalibrationStatusArray>(
    isHubFlow ? initialHubCalibrationData : initialNoHubCalibrationData,
  );

  const { sensorFailedCalibration, setBatteryLevel, state } = useSensorsContext();
  const { config } = useSystemConfigContext();

  const service = isHubFlow ? hubSensorService : sensorService;

  const batteryLevel = useMemo(() => {
    if (!sensorId) return -1;

    return state.batteryLevel.get(sensorId) ?? -1;
  }, [state.batteryLevel, sensorId]);

  useEffect(() => {
    function handleResetCalibration() {
      setCalibrationData(isHubFlow ? initialHubCalibrationData : initialNoHubCalibrationData);
    }

    service.on('calibrationReset', handleResetCalibration);

    return () => {
      service.off('calibrationReset', handleResetCalibration);
    };
  }, [sensorId, service]);

  useEffect(() => {
    if (!sensorId) return;

    if (YG_DEV_MISSING_SENSORS.includes(sensorId)) {
      setBatteryLevel(sensorId, 65);
      return;
    }

    service.on('calibrationStarted', () => {
      setCalibrationData(isHubFlow ? initialHubCalibrationData : initialNoHubCalibrationData);
    });

    service.on('calibrationPacket', (event): void => {
      if (event.macAddress !== sensorId) {
        return;
      }

      let hasProcessedPacket = false;

      setCalibrationData((prev) => {
        return prev.map((status) => {
          if (status !== 'notStarted' || hasProcessedPacket) return status;

          hasProcessedPacket = true;

          if (event.movement > (config?.calibrationMovementLimit ?? 2)) {
            sensorFailedCalibration(sensorId);
            return 'error';
          }

          return 'success';
        }) as CalibrationStatusArray;
      });
    });
  }, [sensorId, service]);

  useEffect(() => {
    function onCalibrationFinished() {
      if (!sensorId) return;

      if (YG_DEV_MISSING_SENSORS.includes(sensorId)) {
        setCalibrationData(YG_DEV_MISSING_SENSORS.map((macAddress) => 'success') as CalibrationStatusArray);
        return;
      }

      if (calibrationData.includes('notStarted') || calibrationData.includes('error')) {
        sensorFailedCalibration(sensorId);

        setCalibrationData((prev) => {
          return prev.map((status) => {
            if (status === 'notStarted') {
              return 'error';
            }

            return status;
          }) as CalibrationStatusArray;
        });
      }
    }

    service.on('calibrationFinished', onCalibrationFinished);

    return () => {
      service.off('calibrationFinished', onCalibrationFinished);
    };
  }, [service, calibrationData, sensorId]);

  return {
    calibrationData,
    batteryLevel,
  };
}
